import { Alert, CircularProgress, IconButton, Popover, Table, TableBody, TableCell, TableHead, TableRow, Tooltip } from "@mui/material";
import Euro from "@mui/icons-material/Euro";
import { useLazyQuery } from "@apollo/client";
import usePopover from "components/hooks/usePopover";
import { intl } from "LocaleWrapper";
import { GetPjkRatesDocument } from "__generated__/view/agreements/graphql/pjk.queries.generated";
import { DataFormat } from "utils";
import { FormattedDate } from "utils/DateUtils";

type PjkRateActionProps = {
	agreementId: number;
	voyageId: number;
};

const PjkRateAction = ({ agreementId, voyageId }: PjkRateActionProps) => {
	const { popoverProps, onPopoverOpen } = usePopover({ trigger: "click" });
	const [ getPjkRates, { data, loading, error } ] = useLazyQuery(GetPjkRatesDocument);

	const handleOpen: React.MouseEventHandler<HTMLButtonElement> = (event) => {
		onPopoverOpen(event);
		getPjkRates({ variables: { agreementId, voyageId } });
	};

	const rates = data?.pjkRates ?? [];

	return <>
		<Tooltip title={intl.formatMessage({ id: "PJK" })}>
			<IconButton size="small" onClick={handleOpen}>
				<Euro fontSize="small" />
			</IconButton>
		</Tooltip>
		<Popover {...popoverProps}>
			<div style={{ padding: 16, minWidth: 360 }}>
				{loading && <CircularProgress size={20} />}
				{error && <Alert severity="error">{error.message}</Alert>}
				{!loading && !error && rates.length === 0 && (
					<span>{intl.formatMessage({ id: "NO_DATA" })}</span>
				)}
				{!loading && !error && rates.length > 0 && (
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell>{intl.formatMessage({ id: "REFERENCE_RATE_ROUTE" })}</TableCell>
								<TableCell>{intl.formatMessage({ id: "PJK_TARIFF" })}</TableCell>
								<TableCell>{intl.formatMessage({ id: "DATE" })}</TableCell>
								<TableCell align="right">{intl.formatMessage({ id: "PRICE" })}</TableCell>
								<TableCell align="right">{intl.formatMessage({ id: "WEIGHT" })}</TableCell>
								<TableCell align="right">{intl.formatMessage({ id: "REVENUE" })}</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{rates.map((rate, index) => (
								<TableRow key={index}>
									<TableCell>{rate.route ?? "-"}</TableCell>
									<TableCell>{rate.rate ? intl.formatMessage({ id: `PJK_TARIFF_${rate.rate}` }) : "-"}</TableCell>
									<TableCell>{rate.blDate ? <FormattedDate date={rate.blDate} format="ll" /> : "-"}</TableCell>
									<TableCell align="right">{rate.price ? DataFormat.price(rate.price) : "-"}</TableCell>
									<TableCell align="right">{rate.weight ? DataFormat.tonnage(rate.weight) : "-"}</TableCell>
									<TableCell align="right">{rate.revenue ? DataFormat.price(rate.revenue) : "-"}</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				)}
				{rates.flatMap(r => r.warnings).map((warning, i) => (
					<Alert key={i} severity="warning" sx={{ marginTop: 1 }}>{warning}</Alert>
				))}
			</div>
		</Popover>
	</>;
};

export default PjkRateAction;
